import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Chip
} from '@mui/material';
import { formatarMoeda } from '../../utils/formatters';
import { Check as CheckIcon } from '@mui/icons-material';

const ConfirmacaoPagamentoPublico = ({
  open,
  onClose,
  pagamento,
  loading,
  error,
  success,
  onConfirmar
}) => {
  const formatarDetalhesMetodo = () => {
    if (!pagamento) return { titulo: '', detalhes: '' };
  
    const dadosPagamento = pagamento.dadosPagamentoJSON || {};
  
    switch (pagamento.metodoPagamento) {
      case 'MPESA':
      case 'EMOLA':
        return {
          titulo: pagamento.metodoPagamento,
          detalhes: dadosPagamento.numeroTelefone 
            ? `Número: +258 ${dadosPagamento.numeroTelefone}`
            : 'Número não fornecido'
        };
  
      case 'BCI':
      case 'MOZABANK':
      case 'STANDARDBANK':
        return {
          titulo: pagamento.metodoPagamento,
          detalhes: `Entidade: ${dadosPagamento.entidade || 'N/A'}\nReferência: ${dadosPagamento.referencia || 'N/A'}`
        };
  
      case 'CARTAO':
        return {
          titulo: 'Cartão de Crédito/Débito',
          detalhes: dadosPagamento.numero 
            ? `Cartão: **** **** **** ${dadosPagamento.numero.slice(-4)}\nValidade: ${dadosPagamento.validade}`
            : 'Dados do cartão não disponíveis'
        };
  
      default:
        return {
          titulo: pagamento.metodoPagamento || 'Método não especificado',
          detalhes: 'Sem detalhes adicionais'
        };
    }
  };

  const renderConteudo = () => {
    // Prioridade: Status 'CONFIRMADO' tem precedência sobre 'success'
    if (pagamento?.status === 'CONFIRMADO') {
      return (
        <Stack spacing={2} alignItems="center">
          <Box sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <CheckIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography variant="h6" align="center" color="success.main">
            Pagamento Realizado com Sucesso!
          </Typography>
          <Typography color="text.secondary" align="center">
            Seu pagamento foi processado e confirmado.
          </Typography>
          
          {/* Botão para voltar ao início */}
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.href = process.env.FRONTEND_URL}
            sx={{ mt: 2 }}
          >
            Voltar ao Início
          </Button>
        </Stack>
      );
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (success) {
      return (
        <Stack spacing={2} alignItems="center">
          <Box sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <CheckIcon sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          <Typography variant="h6" align="center">
            Pagamento Registrado com Sucesso!
          </Typography>
          <Typography color="text.secondary" align="center">
            Aguardando confirmação do operador
          </Typography>
        </Stack>
      );
    }

    if (!pagamento) {
      return (
        <Alert severity="warning">
          Dados do pagamento não disponíveis
        </Alert>
      );
    }

    const { titulo, detalhes } = formatarDetalhesMetodo();

    return (
      <Stack spacing={2}>
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Detalhes da Prestação
          </Typography>
          <Typography variant="body2">
            {pagamento.prestacao?.mes || 'Mês não especificado'}
          </Typography>
          <Typography variant="h6" color="primary">
            {formatarMoeda(pagamento.valorTotal || 0)}
          </Typography>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Método de Pagamento
          </Typography>
          <Typography variant="body1">{titulo}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {detalhes}
          </Typography>
        </Paper>

        {pagamento.valorMulta > 0 && (
          <Alert severity="warning">
            Este valor inclui multa de {formatarMoeda(pagamento.valorMulta)}
          </Alert>
        )}

        {pagamento.status && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">Status:</Typography>
            <Chip 
              label={pagamento.status} 
              color={pagamento.status === 'CONFIRMADO' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        )}
      </Stack>
    );
  };

  const renderActions = () => {
    if (pagamento?.status === 'CONFIRMADO') {
      // Não exibir ações adicionais quando o pagamento está confirmado
      return null;
    }

    if (loading) {
      // Não exibir ações durante o carregamento
      return null;
    }

    if (success) {
      return (
        <Button onClick={onClose} variant="contained">
          Fechar
        </Button>
      );
    }

    return (
      <>
        <Button onClick={onClose}>
          Cancelar
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={onConfirmar}
          disabled={loading || !pagamento}
        >
          Confirmar Pagamento
        </Button>
      </>
    );
  };

  const getDialogTitle = () => {
    if (pagamento?.status === 'CONFIRMADO') {
      return 'Confirmação de Pagamento';
    }
    return success ? 'Confirmação' : 'Confirmar Pagamento';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>
      
      <DialogContent>
        {renderConteudo()}
      </DialogContent>

      <DialogActions>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
};

ConfirmacaoPagamentoPublico.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmar: PropTypes.func.isRequired,
  pagamento: PropTypes.shape({
    id: PropTypes.number,
    metodoPagamento: PropTypes.string,
    valorTotal: PropTypes.number,
    valorMulta: PropTypes.number,
    status: PropTypes.string,
    dataPagamento: PropTypes.string,
    dadosPagamentoJSON: PropTypes.object,
    prestacao: PropTypes.shape({
      id: PropTypes.number,
      mes: PropTypes.string
    })
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  success: PropTypes.bool
};

export default ConfirmacaoPagamentoPublico;
